/**
 * «Скопіювати документ» (ч.1267, прохання бухгалтерів): новий документ з тими самими даними.
 *
 * Адреса `…/new?copy=<id зразка>`. Редактор нового документа завантажує зразок і заповнює
 * форму ТИМ САМИМ кодом, яким відкриває наявний документ, а потім скидає те, що в копії має
 * бути своїм: номер, дата, привʼязки до замовлень і підстав. Копіювати можна і проведений, і
 * непроведений документ — копія завжди нова й непроведена.
 *
 * `source` — зразок, поки його не застосовано; `done()` — застосовано: параметр `copy`
 * прибирається з адреси, щоб оновлення сторінки не перезаповнило форму поверх правок.
 */
import { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useSearchParams } from 'react-router-dom';

import { clearDraft, signalUserEdit } from '@/store/formDrafts';

export function useDocCopy<D>(isNew: boolean, key: string, load: (id: string) => Promise<D>) {
  const [sp, setSp] = useSearchParams();
  const { pathname } = useLocation();
  const copyId = isNew ? sp.get('copy') : null;
  // Стара чернетка «нового документа» на цій адресі копії не потрібна: інакше форма
  // запропонувала б «відновити незбережені зміни» поверх щойно скопійованих даних.
  useEffect(() => { if (copyId) clearDraft(pathname); }, [copyId, pathname]);
  const q = useQuery({
    queryKey: [key, 'copy-source', copyId],
    queryFn: () => load(copyId!),
    enabled: !!copyId,
    staleTime: Infinity,
  });
  const [applied, setApplied] = useState<string | null>(null);
  const source: D | null = copyId && q.data && applied !== copyId ? q.data : null;
  const done = useCallback(() => {
    if (!copyId) return;
    setApplied(copyId);
    const next = new URLSearchParams(sp);
    next.delete('copy');
    setSp(next, { replace: true });
    // скопійовані дані — це вже «правки»: вони мають пережити перехід на іншу вкладку
    window.setTimeout(signalUserEdit, 0);
  }, [copyId, sp, setSp]);
  return { source, done, failed: !!copyId && q.isError };
}

/** Поточний момент у форматі поля дати документа (як у порожній формі редакторів). */
export const nowForDocDate = (): string => new Date().toISOString().slice(0, 16);

/** Дія «скопіювати» у рейці документа («Повʼязані дії»). */
export const copyRailAction = (isNew: boolean, go: () => void) => ({
  label: 'скопіювати',
  title: isNew
    ? 'Спершу збережіть документ'
    : 'Новий документ з тими самими даними: свій номер і сьогоднішня дата, без проведення',
  disabled: isNew,
  onClick: go,
});
